import { ensure, isEmpty, isNone, maybe, objectEmptyOrNone, objectEntries, Pair } from '../util.js';
import { Decimal, Id, Maybe, SizeChart, Timestamp } from '../data.js';
import { DomainId } from './domains.js';
import { ContentItem } from './content.js';
import { MainColorGroup } from './colors.js';
import {
	AgeRangeDescription,
	AmazonDepartment,
	FitType,
	Material,
	NeckStyle,
	SleeveCuffStyle,
	SleeveType,
	TargetGender,
} from './product-details.js';
import { TierCategory } from './stores.js';

export const sizeGroups: Record<string, string> = {
	W: 'Women\'s',
	A: 'Adult',
	G: 'Girls',
	Y: 'Youth',
	I: 'Infant',
	T: 'Toddler',
	O: 'One size fits all',
	S: 'Shoe',
};
export const sizeGroupOrder = Object.keys(sizeGroups);

const smallSizes = ['XS', 'S', 'M', 'L', 'XL'];
export const sizeList = [...smallSizes, '2XL', '3XL', '4XL', '5XL', '6XL'];
const infantSizes = ['3M', '6M', '12M', '18M', '24M'];
const toddlerSizes = ['2T', '3T', '4T', '5/6'];

export const shoeSizes: string[][] = [
	['1/2', 'Youth 1/2'],
	...[3, 4, 5, 6].map(s => [`${s}`, `Youth ${s} / Women's ${s + 1}`]),
	...[7, 8, 9, 10, 11, 12, 13].map(s => [`${s}`, `Men's ${s} / Women's ${s + 1}`]),
	...[14, 16].map(s => [`${s}`, `Men's ${s}/${s + 1}`]),
];

export const allPossibleSizes = [
	...sizeList.map(s => `W-${s}`),
	...sizeList.map(s => `A-${s}`),
	...smallSizes.map(s => `G-${s}`),
	...smallSizes.map(s => `Y-${s}`),
	...infantSizes.map(s => `I-${s}`),
	...toddlerSizes.map(s => `T-${s}`),
	...shoeSizes.map(s => `S-${s[0]}`),
	'OSFA',
];

export interface Point {
	x?: number; // -1 .. 1
	y?: number; // 0 .. 1
	w?: number;
	h?: number;
	rotation?: number;
	area?: string;
	side?: string;
	left?: Point;
	right?: Point;
	visible?: boolean;
	adjust?: boolean;
	cover?: boolean;
	alignX?: 'left' | 'center' | 'right';
	alignY?: 'top' | 'center' | 'bottom';
}

export interface ProductArea {
	sides: Record<string, ProductAreaSide>;
}

export interface ProductAreaSide {
	type?: 'poly' | 'rect';
	points?: Pair<number>[];
	topLeft?: Pair<number>;
	bottomRight?: Pair<number>;
	rotate?: number;
	wrapAxes?: Pair<Pair<number>>[];
}

export interface PublicProduct {
	id: string & { __entity?: 'Product' };
	flow: string;
	name: string;
	price: Decimal; // FIXME actually missing in a few outliers
	available: boolean;
	visible: boolean;
	discontinued: boolean;
	categories: string[];
	tier_category: Maybe<Id<TierCategory>>;
	size_chart: Maybe<Id<SizeChart>>;
	priority: number;
	physical: boolean;
	brand_name: Maybe<string>;
	image_size: Maybe<Pair<number>>;
	include_reviews_from: Maybe<Id<Product>[]>;
	is_product: boolean;
	is_design_service: boolean;
	can_reorder: boolean;
	/** Virtual, computed in cleanUpProduct */
	alias?: Maybe<string>;
	description?: Maybe<ContentItem>;
}

export interface ComputedProductInfo {
	min_order_quantity: Maybe<number>;
	price_range: Maybe<[Decimal, Decimal]>;
	moq_price: Maybe<Decimal>; // FIXME probably
	min_prod_days: Maybe<number>;
	max_prod_days: Maybe<number>;
	popularity: Maybe<number>;
	pd_count: Maybe<number>;
	category_priority: Maybe<number>;
	total_priority: Maybe<number>;
	templates_are_colors: Maybe<boolean>;
	mask_image: Maybe<string>;
	preview_image: Maybe<ProductImage>;
	alias: Maybe<string>;
	info_url: Maybe<string>;
}

export interface ExtraProductInfo {
	page_title: Maybe<string>;
	description?: Maybe<ContentItem>;
	overview: Maybe<ContentItem>;
	information: Maybe<ContentItem>;
	bullet_points: Maybe<string[]>;
	meta_description: Maybe<string>;
	meta_keywords: Maybe<string>;
}

export interface WithFeatures {
	features: Maybe<ProductFeatures>;
}

export type ComputedProduct = PublicProduct & ComputedProductInfo;
export type FullPublicProduct = ComputedProduct & ExtraProductInfo & WithFeatures;

export interface Product extends PublicProduct, ExtraProductInfo, WithFeatures, Partial<ComputedProductInfo> {
	created: Timestamp;
	updated: Timestamp;
	replaced_with?: Maybe<Id<Product>>;
	weight_oz?: number;
	weight_by_size: Record<string, Maybe<number>>;
	paypal_category?: string;
	customs_description?: string;
	default_template?: string;
	domains: Maybe<string[]>;
	sanmar_product?: string;
	cost?: Maybe<number>;
	default_stitch_count?: Maybe<number>;
	overrides: Maybe<ProductOverride[]>;
	aliases: string[];
	google_product_category: Maybe<number>;
	lock_editing: boolean;
	needs_work: boolean;
}

export interface ProductOverride {

	id: string & { __entity?: 'ProductOverride' };
	priority?: number; // priority for the rule itself!

	// match
	dimensions?: Maybe<Record<Id<ProductCategoryDimension>, Id<ProductCategory>[]>>;
	domain?: Maybe<DomainId>;

	// set
	name?: Maybe<string>;
	alias?: Maybe<string>;
	sort_priority?: Maybe<number>;
	brand_name?: Maybe<string>;
	description?: Maybe<ContentItem>;
	overview?: Maybe<ContentItem>;
	information?: Maybe<ContentItem>;
	page_title?: Maybe<string>;
	meta_description?: Maybe<string>;
	meta_keywords?: Maybe<string>;
	discount_formula?: Maybe<string>;
	price?: Maybe<Decimal>;
	size_markups?: Maybe<Record<string, Maybe<number>>>;
	google_product_category?: Maybe<number>;
	
}

export type ProductOverrideList = ProductOverride[];

export interface ProductFeatures {

	/**
	 * Minimum quantity of pieces that can be ordered.
	 */
	minOrderQuantity: Maybe<number>;

	/**
	 * The quantity can only be a multiple of this.
	 */
	quantityDivisor: Maybe<number>;

	/**
	 * Is there a roster to be filled in? If not, just a single "quantity" field, no sizes or customization.
	 */
	roster: boolean;

	/**
	 * SKU to use when ordering blanks. Can be a string (same for all) or object indexed by "gender"
	 */
	sku: Maybe<string | Record<string, string>>;

	/**
	 * List of sizes in G-S format. At least "O" must be present if roster is enabled.
	 */
	sizes: string[];

	/**
	 * Is a mask image used for the preview? (the black/white thing)
	 */
	mask: boolean;

	/**
	 * Reuse mask image from another product.
	 */
	maskImage: Maybe<string>;
	
	maskFeatures: Maybe<{
		clip?: Maybe<boolean>;
		commands?: Maybe<boolean>;
		colors?: Maybe<MainColorGroup[]>;
	}>;

	/**
	 * Can a flag part be added?
	 */
	flag: boolean;
	/**
	 * Can name/number/alias be added?
	 */
	nameNumber: boolean;
	/**
	 * Is there a PW logo enforced on the design?
	 */
	pwLogo: boolean;

	/**
	 * Should the "popular colors" thing be shown?
	 */
	popularColors: boolean;

	/**
	 * Are "templates" actually fabric colors? (and should be previewed as such)
	 */
	templatesAreColors: boolean;

	/**
	 * Disallow adding more than one image. Best used with logoPositionLocked.
	 */
	maxOneLogo: boolean;
	/**
	 * Disallow changing the position (but not size) of any images/text.
	 */
	logoPositionLocked: boolean;

	/**
	 * List of canvas sizes to use, biggest to smallest.
	 * - must all be the same aspect ratio
	 * - may not be changed after product goes public
	 */
	imageSizes: Maybe<Pair<number>[]>;

	/**
	 * List of default/available positions for various parts. Idk too convoluted to document properly for now.
	 * The unit used for *both* Y and X dimensions is "heights". Meaning height of the canvas = 1 unit. Width will often be = 1.333...
	 */
	points: Maybe<Record<string, Point | null>>;

	/**
	 * Which sides the product has. Can *only* be ['front'] or ['front', 'back'], any other configurations may result in Zalgo.
	 */
	sides: Maybe<string[]>;

	/**
	 * Areas where parts can go. Should have at least 'main'.
	 */
	areas: Maybe<ProductAreas>;
	
	/**
	 * The scale of how many inches are per whichever number works (same unit as the x/y/w/h coordinates)
	 */
	physicalScale: Maybe<{
		inches: number;
		per: number;
	}>;

	/**
	 * Which point should the PW logo start at when present.
	 */
	defaultPWPosition: Maybe<string>;

	/**
	 * Maximum size of *one* image in "height units".
	 */
	maxLogoSize: Maybe<[number, number]>;

	/**
	 * Which color groups (out of ['base', 'primary', 'secondary', 'accent', 'collar']) are available for the product.
	 * Overriden by specific ProductTemplates.
	 */
	colorGroups: Maybe<MainColorGroup[]>;

	// Oddly specific use cases below

	/**
	 * Separate price when an add-on service. Used only for `vector` and `digitize`.
	 */
	addOnCost?: Maybe<number>;
	
	/**
	 * Require vectors or charge a fee (DPKG ONLY)
	 */
	requireVector?: Maybe<boolean>;
	
	/**
	 * Features used only by dpkg products.
	 */
	designPackage?: Maybe<DesignPackageFeatures>;
	
	/**
	 * Generate a gift card for this amount when purchasing the product.
	 */
	includeGiftCard?: Maybe<Decimal>;

	/**
	 * Product revision (stored in 'apparel' part, used to indicate a need for some conversion)
	 */
	revision?: Maybe<number>;

	/**
	 * Type of fabric the product is made of. (Required for Amazon listings)
	 */
	fabricType?: Maybe<string>;

	/**
	 * Neck cut style/shape. (Required for Amazon listings)
	 */
	neckStyle?: Maybe<NeckStyle>;

	/**
	 * Age group the product is suited for. (Required for Amazon listings)
	 */
	ageRangeDescription?: Maybe<AgeRangeDescription>;

	/**
	 * Type of fit. (Required for Amazon listings)
	 */
	fitType?: Maybe<FitType>;

	/**
	 * Sleeve cuff cut style/shape. (Required for Amazon listings)
	 */
	sleeveCuffStyle?: Maybe<SleeveCuffStyle>;

	/**
	 * Type of sleeve. (Required for Amazon listings)
	 */
	sleeveType?: Maybe<SleeveType>;

	/**
	 * Gender group the product is suited for. (Required for Amazon listings)
	 */
	targetGender?: Maybe<TargetGender>;

	/**
	 * Material the product is made from. (Required for Amazon listings)
	 */
	material?: Maybe<Material>;

	/**
	 * Amazon department the product belongs to. (Required for Amazon listings)
	 */
	amazonDepartment?: Maybe<AmazonDepartment>;
	
}

export type ProductAreas = Record<string, ProductArea>;

export interface DesignPackageFeatures {

	/**
	 * Products included in a design package. Unused by anything else.
	 */
	productsIncluded?: Maybe<number | 'all'>;
	
	/**
	 * Display products from these categories to choose in a design package.
	 */
	productsIncludedCategories?: Maybe<string[]>;
	
	/**
	 * Display products from these autofill kits to choose in a design package.
	 */
	productsIncludedKits?: Maybe<string[]>;
	
	/**
	 * Product selection mode for DPKG.
	 */
	selectProductsBy?: Maybe<'product' | 'kit'>;
	
	/**
	 * Sublimation template colors required for a design package.
	 * Previously designPackageColors
	 */
	designColors?: Maybe<number>;
	
	/**
	 * Stock fabric colors required for a design package.
	 */
	stockColors?: Maybe<number>;
	
	/**
	 * Enable design package template selection.
	 */
	templateSelection?: Maybe<DesignPackageTemplateSelection>;
	
}

export interface DesignPackageTemplateSelection {
	/** Product to use for template previes. */
	product: Id<Product>;
	/** Require that at least one template is chosen. */
	required?: Maybe<boolean>;
	/** Maximum number of templates that can be chosen, 1 by default. */
	max?: Maybe<number>;
}

export interface ProductImage {

	id: string & { __entity?: 'ProductImage' };
	product: Id<Product>;
	domains: Maybe<DomainId[]>;
	image: Maybe<string>;
	design: Maybe<string>;
	caption: Maybe<string>;
	priority: number;
	main: boolean;

	created: Timestamp;
	updated: Timestamp;
	deleted: boolean;

	template_preview?: true;
	sanmar_preview?: string;
	path?: Maybe<string>;

}

export function getSKU(product: PublicProduct & WithFeatures, size: string) {
	const sku = product.features?.sku;
	if (!sku || typeof sku == 'string') return sku || product.id;
	const sp = size.split('-');
	return sku[sp[0]!] || sku[size] || product.id;
}

export function getSizeName(size: string, withGroup = true) {
	const sp = size.split('-');
	if (sp[0] == 'S') return maybe(shoeSizes.find(ss => ss[0] == sp[1]), ss => ss[1]) || sp[1]!;
	return `${(withGroup || !sp[1]) ? sizeGroups[sp[0]!] || '' : ''} ${sp[1] || ''}`.trim() || size;
}

export function getSizeAbbr(size: string) {
	const sp = size.split('-');
	if (['Y', 'G'].includes(sp[0]!)) return sp[0] + (sp[1] || '');
	return sp[1] || sp[0];
}

export function getSizeGroup(size: string) {
	return size === 'O' ? 'OSFA' : ensure(size.split('-')[0]);
}
export function getSizeOnly(size: string) {
	return size === 'O' ? 'OSFA' : ensure(size.split('-')[1]);
}

export function getRosterSizeSort(sizeStr: Maybe<string>) {
	const valid = (idx: number) => idx >= 0 ? idx : null;
	if (isNone(sizeStr)) return allPossibleSizes.length;
	return (
		valid(allPossibleSizes.indexOf(sizeStr))
		?? valid(allPossibleSizes.indexOf(sizeStr.split('/')[0]!))
		?? valid(allPossibleSizes.findLastIndex(s => s.split('-')[0] == sizeStr.split('-')[0]))
		?? allPossibleSizes.length
	);
}

export interface ProductAlias {
	id: string & { __entity?: 'ProductAlias' };
	alias: string;
	domain: DomainId;
	product: Id<Product>;
	base: string;
	conflicts: Maybe<Id<Product>[]>;
	dimensions: Record<Id<ProductCategoryDimension>, Id<ProductCategory>>;
	active: boolean;
	fulltext: unknown; // tsvector magic
	created: Timestamp;
	updated: Timestamp;
}

export type FlowDecorationType = 'cut-and-sew' | 'dtf' | 'sub-blanks' | 'embroidery' | 'screen-print';
export const flowDecorationTypes: FlowDecorationType[] = ['cut-and-sew', 'dtf', 'sub-blanks', 'embroidery', 'screen-print'];
export const flowDecorationTypesWithFee: FlowDecorationType[] = ['dtf', 'embroidery', 'screen-print'];

export type FlowGraphicsType = 'vector' | 'digitize' | 'screen-print';
export const flowGraphicsTypes: FlowGraphicsType[] = ['vector', 'digitize', 'screen-print'];

export const flowPrintFileTypes = ['none', 'design', 'size', 'roster', 'area'] as const;
export type FlowPrintFileType = (typeof flowPrintFileTypes)[number];

export interface ProductCategory {
	id: string & { __entity?: 'ProductCategory' };
	url: Maybe<string>;
	dimension: Id<ProductCategoryDimension>;
	name: string;
	title: Maybe<string>;
	description: Maybe<ContentItem>;
	substitution: Maybe<string>;
	meta_description?: Maybe<string>;
	meta_keywords?: Maybe<string>;
	visible: boolean;
	priority: number;
	product_priority: number;
	domains: Maybe<string[]>;
	overrides: null | Partial<Record<DomainId, ProductCategoryOverride>>;
}

export interface ProductCategoryOverride {
	priority?: Maybe<number>;
	name?: Maybe<string>;
	title?: Maybe<string>;
	substitution?: Maybe<string>;
	product_priority?: Maybe<number>;
	description?: Maybe<ContentItem>;
	meta_description?: Maybe<string>;
	meta_keywords?: Maybe<string>;
}

export interface ProductCategoryDimension {
	id: string & { __entity?: 'ProductCategoryDimension' };
	name: string;
	priority: number;
	visible: boolean;
	deleted?: boolean;
	description?: string;
	domains: Maybe<string[]>;
	overrides: null | Partial<Record<DomainId, {
		priority?: Maybe<number>;
		name?: Maybe<string>;
		visible?: Maybe<boolean>;
	}>>;
}

export type PresentCategoryMap = Record<Id<ProductCategoryDimension>, Record<Id<ProductCategory>, ProductCategory>>;

export function presentCategoriesByDimension(cats: ProductCategory[]) {
	const map: PresentCategoryMap = {};
	for (const cat of cats) {
		map[cat.dimension] ??= {};
		map[cat.dimension]![cat.id] = cat;
	}
	return map;
}
export function overrideDimensionsMatchMap(override: ProductOverride, map: PresentCategoryMap) {
	if (objectEmptyOrNone(override.dimensions)) return true;
	return objectEntries(override.dimensions).every(
		([dim, list]) => isEmpty(list) || list.some(cid => map[dim]?.[cid])
	);
}

export const topLevelDimensions = ['type', 'sport'];

export const productImageThumbSizes = [128, 512, 1024] as const;

export const productSortTypes = [
	'popularity',
	'relevance',
	'priceLow',
	'priceHigh',
] as const;
export type ProductSortType = (typeof productSortTypes)[number];

export interface GoogleProductCategory {
	id: number & { __entity?: 'GoogleProductCategory' };
	name: string;
}
